import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { fonts } from '@/theme/fonts';
import { getLicenseGenerateData } from '@/api/license-list/modal/getLicenseGenerateData';
import { R2GATE_MODULE_TYPE } from '@/api/type/commonTypes';
import { GetLicenseGenerateDataResponse } from '@/api/type/types';
import { postCreateLicenseKeys } from '@/api/license-list/modal/postCreateLicenseKeys';
import { useDispatch } from 'react-redux';
import { license } from '@/stores/slices/license-list/licenseSlice';
import { useTranslation } from 'react-i18next';

interface LicenseIssueModalProps {
  open: boolean;
  onClose: () => void;
  fetchList: () => void;
}

const LicenseIssueModal = ({
  open,
  onClose,
  fetchList,
}: LicenseIssueModalProps) => {
  const [generateData, setGenerateData] =
    useState<GetLicenseGenerateDataResponse>({
      issuerName: '',
      modules: [],
    });
  const [reason, setReason] = useState<string>('');
  const [selectedModule, setSelectedModule] =
    useState<R2GATE_MODULE_TYPE>('FACEGIDE');
  const [count, setCount] = useState<number | null>(null);
  const [keys, setKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onClickModule = useCallback(
    (item: R2GATE_MODULE_TYPE) => () => {
      if (keys.length > 0) {
        const isConfirm = confirm(
          t('licenseManagement.messages.moduleChangeWarning')
        );
        if (!isConfirm) return;
      }
      setSelectedModule(item);
      setKeys([]);
      setCount(null);
      setReason('');
    },
    [keys.length, t]
  );

  const handleIssue = useCallback(async () => {
    if (reason === '') {
      alert(t('licenseManagement.messages.issueReasonRequired'));
      return;
    }
    if (!count) {
      alert(t('licenseManagement.messages.issueQuantityRequired'));
      return;
    }
    if (count > 10) {
      alert(t('licenseManagement.messages.issueQuantityLimit'));
      return;
    }
    try {
      setIsLoading(true);
      const res = await postCreateLicenseKeys({
        module: selectedModule,
        count: count,
        reason: reason,
      });
      if (res.isSuccess) {
        fetchList();
        // dispatch(
        //   license.addLicenseItems({
        //     keys: res.data.licenseKeyIds,
        //     module: selectedModule,
        //     issuerName: generateData.issuerName,
        //     reason: reason,
        //   })
        // );
        setKeys(res.data.licenseKeyIds);
      }
    } finally {
      setIsLoading(false);
    }
  }, [count, dispatch, generateData.issuerName, reason, selectedModule, t]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(keys.join('\n'));
    alert(t('licenseManagement.messages.copySuccess'));
  }, [keys, t]);

  const onHandleCloseModal = useCallback(() => {
    if (keys.length > 0) {
      const isConfirm = confirm(
        t('licenseManagement.messages.exitIssueConfirm')
      );
      if (!isConfirm) return;
    }
    onClose();
  }, [keys.length, onClose, t]);

  const onChangeCount = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');

      if (value === '') {
        setCount(null);
        return;
      }

      setCount(Number(value));
    },
    []
  );

  const fetchGenerateData = useCallback(async () => {
    const res = await getLicenseGenerateData();
    if (res.isSuccess) {
      console.log('?');
      setGenerateData({
        issuerName: res.data.issuerName,
        modules: res.data.modules,
      });
    }
  }, []);

  useEffect(() => {
    fetchGenerateData();
  }, [fetchGenerateData]);

  useEffect(() => {
    if (!open) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('onclose', keys);
        onHandleCloseModal();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onHandleCloseModal, keys]);

  if (!open) return null;

  return (
    <Overlay onClick={onHandleCloseModal}>
      <ModalWrap onClick={(e) => e.stopPropagation()}>
        <Title>{t('licenseManagement.issueLicenseKey')}</Title>
        <Section>
          <Row>
            <Wrap $width="200px">
              <Label>{t('licenseManagement.issueForm.issuer')}</Label>
              <Name>{generateData.issuerName}</Name>
            </Wrap>
            <Wrap>
              <Label>{t('licenseManagement.issueForm.issueReason')}</Label>
              <Input
                maxLength={30}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Wrap>
          </Row>
          <Wrap>
            <SectionTitle>
              {t('licenseManagement.issueForm.moduleType')}
            </SectionTitle>
            <ModuleRow>
              {generateData.modules.map((item) => (
                <ModuleButton
                  key={item}
                  $active={selectedModule === item}
                  onClick={onClickModule(item)}
                >
                  {item}
                </ModuleButton>
              ))}
            </ModuleRow>
          </Wrap>
          <Wrap $width="400px">
            <SectionTitle>
              {t('licenseManagement.issueForm.issueQuantity')}
            </SectionTitle>
            <IssueRow>
              <CountInput
                type="text"
                inputMode="numeric"
                value={count ?? ''}
                onChange={onChangeCount}
              />
              <IssueButton onClick={handleIssue} disabled={isLoading}>
                {isLoading
                  ? t('licenseManagement.messages.issuing')
                  : t('licenseManagement.messages.issue')}
              </IssueButton>
            </IssueRow>
            <ResultBox>
              {keys.map((item) => (
                <div key={item}>{item}</div>
              ))}
            </ResultBox>

            <CopyButton disabled={keys.length === 0} onClick={handleCopy}>
              {t('licenseManagement.copyLicenseKey')}
            </CopyButton>
          </Wrap>
        </Section>
        <Footer>
          <CloseButton onClick={onHandleCloseModal}>
            {t('licenseManagement.close')}
          </CloseButton>
        </Footer>
      </ModalWrap>
    </Overlay>
  );
};

export default LicenseIssueModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalWrap = styled.div`
  width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
`;

const Title = styled.div`
  margin-bottom: 24px;
  font-size: 20px;
  font-weight: ${fonts.weight.semiBold};
  color: #111827;
`;

const Row = styled.div`
  display: flex;
  gap: 18px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: ${fonts.weight.medium};
  color: #374151;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

interface WrapProps {
  $width?: string;
}

const Wrap = styled.div<WrapProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${({ $width }) => ($width ? $width : '100%')};
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;

  &:focus {
    border-color: #2563eb;
  }
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: ${fonts.weight.semiBold};
  color: #374151;
`;

const ModuleRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ModuleButton = styled.button<{ $active: boolean }>`
  min-width: 120px;
  height: 40px;
  border-radius: 20px;
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#d1d5db')};
  font-size: 14px;
  font-weight: ${fonts.weight.medium};
  background: ${({ $active }) => ($active ? '#2563eb' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#374151')};
`;

const IssueRow = styled.div`
  display: flex;
  gap: 19px;
`;

const CountInput = styled.input`
  width: 200px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    border-color: #2563eb;
  }
`;

const IssueButton = styled.button`
  width: 180px;
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 700;
  cursor: pointer;
  background: #2563eb;
  &:hover {
    background: #1d4ed8;
  }
`;

const ResultBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  height: 240px;
  font-size: 13px;
  font-family: monospace;
`;

const CopyButton = styled.button`
  width: 100%;
  height: 40px;
  border-radius: 6px;
  border: none;
  background: #2563eb;
  color: white;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    background: #d1d5db;
    cursor: default;
  }
`;

const Footer = styled.div`
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CloseButton = styled.button`
  width: 180px;
  height: 42px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-family: ${fonts.family.poppins};

  &:hover {
    background: #f9fafb;
  }
`;
